import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import FeedConfigResults from './FeedConfigResults';
import { useGlobal } from '../contexts/GlobalContext';
import { useDeploy } from '../contexts/DeployContext';

function ConfigTest({ title = 'Test Feed Config', config, configId }) {
  const { defaultFarcasterUserId, defaultPolymarketWalletAddress } = useGlobal();
  const { deployApi } = useDeploy();

  const [logs, setLogs] = useState([]);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [farcasterFid, setFarcasterFid] = useState(() => defaultFarcasterUserId ?? '');
  const [polymarketWallet, setPolymarketWallet] = useState(() => defaultPolymarketWalletAddress ?? '');
  const [tab, setTab] = useState(0);
  const [cacheAlert, setCacheAlert] = useState(null);
  const [isClearingCache, setIsClearingCache] = useState(false);

  useEffect(() => {
    if (defaultFarcasterUserId != null) setFarcasterFid((prev) => (prev === '' ? defaultFarcasterUserId : prev));
    if (defaultPolymarketWalletAddress != null) setPolymarketWallet((prev) => (prev === '' ? defaultPolymarketWalletAddress : prev));
  }, [defaultFarcasterUserId, defaultPolymarketWalletAddress]);

  const clearResults = useCallback(() => setResults(null), []);
  const clearLogs = useCallback(() => setLogs([]), []);

  const handleRun = useCallback(async () => {
    clearResults();
    clearLogs();
    setError(null);
    if (!config) {
      setLogs(['No Feed Config selected. Select a Feed Config to test.']);
      return;
    }
    const serveConfig = config.feed_config ?? config.deployment_config ?? config;
    const inputs = {
      farcasterFid: farcasterFid?.trim() || undefined,
      polymarketWallet: polymarketWallet?.trim() || undefined,
    };
    setIsRunning(true);
    try {
      const result = await deployApi.servePreview(serveConfig, inputs);
      const resultLogs = result?.result?.logs || [];
      setResults(result?.result ?? null);
      setLogs(resultLogs);
    } catch (err) {
      setError(err?.message ?? String(err));
      setResults(null);
    } finally {
      setIsRunning(false);
    }
  }, [config, deployApi, farcasterFid, polymarketWallet, clearResults, clearLogs]);

  const handleServe = useCallback(async () => {
    clearResults();
    clearLogs();
    setError(null);
    if (configId == null) return;
    const inputs = {
      farcasterFid: farcasterFid?.trim() || undefined,
      polymarketWallet: polymarketWallet?.trim() || undefined,
    };
    setIsRunning(true);
    try {
      const result = await deployApi.serve(configId, inputs);
      const resultLogs = result?.result?.logs || [];
      setResults(result?.result ?? null);
      setLogs(resultLogs);
    } catch (err) {
      setError(err?.message ?? String(err));
      setResults(null);
    } finally {
      setIsRunning(false);
    }
  }, [configId, deployApi, farcasterFid, polymarketWallet, clearResults, clearLogs]);

  const getFeedIdsFromConfig = useCallback((cfg) => {
    const dc = cfg?.feed_config ?? cfg?.deployment_config ?? cfg;
    if (!dc) return [];
    const ids = new Set();
    (dc.main_algos ?? []).forEach((m) => {
      const id = m?.algo_id;
      if (id != null) ids.add(Number(id));
    });
    if (dc.primary_fallback != null) ids.add(Number(dc.primary_fallback));
    if (dc.secondary_fallback != null) ids.add(Number(dc.secondary_fallback));
    return [...ids];
  }, []);

  const handleClearCache = useCallback(async () => {
    setCacheAlert(null);
    if (!config) {
      setCacheAlert({ severity: 'error', message: 'No Feed Config selected. Select a Feed Config to clear cache.' });
      return;
    }
    const feedIds = getFeedIdsFromConfig(config);
    if (feedIds.length === 0) {
      setCacheAlert({ severity: 'error', message: 'Config has no feeds to clear.' });
      return;
    }
    const inputs = {
      farcasterFid: farcasterFid?.trim() || undefined,
      polymarketWallet: polymarketWallet?.trim() || undefined,
    };
    setIsClearingCache(true);
    try {
      const { error, result } = await deployApi.clearCache(feedIds, inputs);
      if (error) {
        setCacheAlert({ severity: 'error', message: error });
        return;
      }
      const lines = Object.entries(result).map(([feedId, { denied, found }]) => {
        if (denied) return `Access was denied to feed #${feedId}`;
        if (found === 0) return `No cache entry found for feed #${feedId}`;
        return `Cleared cache for feed algo #${feedId}`;
      }
      );
      setCacheAlert({ severity: 'success', message: lines.join('\n') });
    } catch (err) {
      setCacheAlert({
        severity: 'error',
        message: err?.message ?? String(err),
      });
    } finally {
      setIsClearingCache(false);
    }
  }, [config, deployApi, farcasterFid, polymarketWallet, getFeedIdsFromConfig]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight="medium">
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                size="small"
                label="Farcaster User Id"
                value={farcasterFid}
                onChange={(e) => setFarcasterFid(e.target.value)}
                placeholder="Farcaster FID"
                fullWidth
              />
              <TextField
                size="small"
                label="Polymarket Wallet"
                value={polymarketWallet}
                onChange={(e) => setPolymarketWallet(e.target.value)}
                placeholder="0x…"
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRun}
                disabled={isRunning}
              >
                Preview
              </Button>
              {configId != null && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleServe}
                  disabled={isRunning}
                >
                  Serve
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearCache}
                disabled={isClearingCache}
              >
                Clear Cache
              </Button>
              {(isRunning || isClearingCache) && <CircularProgress size={24} sx={{ ml: 'auto' }} />}
            </Box>
            {cacheAlert && (
              <Alert
                severity={cacheAlert.severity}
                onClose={() => setCacheAlert(null)}
                sx={{ whiteSpace: 'pre-line' }}
              >
                {cacheAlert.message}
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Results" />
            <Tab label="Logs" />
          </Tabs>
          <Box sx={{ flex: 1, minHeight: 120, p: 2, overflow: 'auto', bgcolor: 'background.paper' }}>
            {tab === 0 && <FeedConfigResults data={results} />}
            {tab === 1 && (
              <Box component="pre" sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {logs.length === 0 ? 'No logs yet.' : logs.join('\n')}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ConfigTest;
